import { ChevronLeft, ChevronRight, Send } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme,
} from '@mui/material';
import React, { ComponentType, useCallback, useState } from 'react';
import api from '../../../services/api';
import { State } from './State';
import { DateRangePicker } from './DateRangePicker';
import { ServiceProviderPicker } from './ServiceProviderPicker';
import { RecipientEmailInput } from './RecipientEmailInput';
import { toaster } from '../../../services/toaster';
import {ReservationSummaries} from "../ReservationSummaries";

export const ReservationCheck: ComponentType = () => {
  const [formState, setFormState] = useState<State>({
    dateRange: [null, null],
    email: '',
  });

  const { serviceProviders, dateRange, email } = formState;

  const [loading, setLoading] = useState(false);

  const fetchServiceProviders = useCallback(() => {
    setLoading(true);
    setFormState({ ...formState, serviceProviders: undefined });

    api
      .get(
        `/service-provider/service-reservation-confirmations/confirmed/all?from=${dateRange[0]?.format(
          'YYYY-MM-DD'
        )}&to=${dateRange[1]?.format('YYYY-MM-DD')}`
      )
      .then((temp1: any) => {
        const serviceProviders = Object.values(
          Object.fromEntries(
            temp1
              .filter(
                (reservation: any) =>
                  reservation.service_provider.dont_send_confirmation === 0
              )
              .map((reservation: any) => [
                reservation.service_provider.id,
                reservation.service_provider,
              ])
          )
        );

        if (serviceProviders.length === 0) {
          toaster.show({
            intent: 'danger',
            message:
              'No confirmed service reservations found in the selected date range.',
          });
        } else {
          setActiveStepIndex(activeStepIndex + 1);
          setFormState({
            ...formState,
            serviceProviders: serviceProviders.map((value) => ({
              value,
              selected: false,
            })),
          });
        }
      })
      .finally(() => setLoading(false));
  }, [dateRange]);

  const [activeStepIndex, setActiveStepIndex] = useState(0);

  const steps = [
    {
      label: 'Select date range',
      component: DateRangePicker,
      forwardButtonIcon: loading ? (
        <CircularProgress color="inherit" size={'1em'} />
      ) : undefined,
      forwardButtonAction: fetchServiceProviders,
      forwardButtonDisabled: dateRange.includes(null) || loading,
    },
    {
      label: 'Select service providers',
      component: ServiceProviderPicker,
      forwardButtonDisabled: !serviceProviders?.some(
        ({ selected }) => selected
      ),
      forwardButtonIcon: loading ? (
        <CircularProgress color="inherit" size={'1em'} />
      ) : (
        <Send />
      ),
      forwardButtonLabel: 'Finish',
      forwardButtonAction: () => {
        setLoading(true);
        api
          .post(
            '/service-provider/service-reservation-confirmations/confirmed/summary',
            {
              email,
              serviceProviderIds: serviceProviders
                ?.filter((serviceProvider) => serviceProvider.selected)
                .map((serviceProvider: any) => serviceProvider.value.id),
              from: dateRange[0]?.format('YYYY-MM-DD'),
              to: dateRange[1]?.format('YYYY-MM-DD'),
            }
          )
          .finally(() => {
            toaster.show({
              intent: 'success',
              message: `Successfully sent confirmation ${
                serviceProviders && serviceProviders.length > 1
                  ? 'summaries'
                  : 'summary'
              }.`,
            });
            setLoading(false);
          });
      },
    },
  ];

  const {
    component: ActiveStepComponent,
    forwardButtonIcon,
    forwardButtonDisabled,
    forwardButtonLabel,
    forwardButtonAction,
    label,
  } = steps[activeStepIndex];

  return (
    <Stack
      sx={{
        alignItems: 'stretch',
        justifyContent: 'space-between',
        flexGrow: 1,
        margin: '10px',
        width: '100%'
      }}
      spacing={1}
    >
      <Stepper activeStep={activeStepIndex}>
        {steps.map(({ label }) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Box sx={{ alignSelf: 'center' }}>
        <Typography variant="h1" align="center" gutterBottom>
          {label}
        </Typography>
        <ActiveStepComponent
          formState={formState}
          setFormState={setFormState}
        />
      </Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <Button
          onClick={() =>
            activeStepIndex > 0 && setActiveStepIndex(activeStepIndex - 1)
          }
          variant="outlined"
          startIcon={<ChevronLeft />}
          disabled={activeStepIndex === 0}
        >
          Back
        </Button>
        <Button
          onClick={
            forwardButtonAction ||
            (() => setActiveStepIndex(activeStepIndex + 1))
          }
          variant="contained"
          endIcon={forwardButtonIcon || <ChevronRight />}
          disabled={forwardButtonDisabled}
        >
          {forwardButtonLabel || 'Next'}
        </Button>
      </Stack>
    </Stack>
  );
};
